import { LogOut, Settings as SettingsIcon, ShieldCheck, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Avatar from '@components/common/Avatar';
import { useAuth } from '@context/AuthContext';
import { useToast } from '@context/ToastContext';
import { PATHS } from '@routes/paths';
import { cn } from '@utils/cn';

export default function ProfileMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Click-outside + ESC dismiss for the dropdown. Cheap to wire up; saves us
  // pulling in a popover library for one menu.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleLogout = async (): Promise<void> => {
    setOpen(false);
    await logout();
    toast.info('Signed out.');
    navigate(PATHS.login, { replace: true });
  };

  if (!user) return null;
  const displayName = user.name || user.email;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open account menu"
        className="rounded-full focus:outline-none focus-visible:shadow-[0_0_0_3px_var(--gold-dim)]"
      >
        <Avatar name={displayName} />
      </button>

      {open ? (
        <div
          role="menu"
          className={cn(
            'absolute right-0 top-[calc(100%+8px)] z-50 w-[260px] origin-top-right animate-screen-in',
            'overflow-hidden rounded-card border border-line bg-surface shadow-md',
          )}
        >
          <div className="flex items-center gap-3 border-b border-line px-4 py-3">
            <Avatar name={displayName} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold text-t1">{displayName}</p>
              <p className="truncate text-[11.5px] text-t3">{user.email}</p>
            </div>
          </div>

          {!user.isEmailVerified ? (
            <div className="flex items-start gap-2 border-b border-line bg-accent-amber-dim px-4 py-2.5 text-[11.5px] text-accent-amber">
              <ShieldCheck size={13} className="mt-0.5 shrink-0" />
              <span>Email not verified — check your inbox.</span>
            </div>
          ) : null}

          <div className="flex flex-col py-1.5">
            <MenuItem
              icon={<User size={13} />}
              label="Profile"
              onClick={() => {
                setOpen(false);
                navigate(PATHS.settings);
              }}
            />
            <MenuItem
              icon={<SettingsIcon size={13} />}
              label="Settings"
              onClick={() => {
                setOpen(false);
                navigate(PATHS.settings);
              }}
            />
            <div className="my-1 h-px bg-line" />
            <MenuItem
              icon={<LogOut size={13} />}
              label="Sign out"
              tone="danger"
              onClick={handleLogout}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  tone?: 'default' | 'danger';
}

function MenuItem({ icon, label, onClick, tone = 'default' }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 px-4 py-2 text-left text-[13px] font-medium transition-colors',
        tone === 'danger'
          ? 'text-accent-red hover:bg-accent-red-dim'
          : 'text-t1 hover:bg-surface-2',
      )}
    >
      <span className={tone === 'danger' ? 'text-accent-red' : 'text-t3'}>{icon}</span>
      {label}
    </button>
  );
}
